import { isToday } from 'date-fns'

import { formatDateLong, formatPrice } from '@/utils'

const getFilteredBillingsByType = (company, withCreditMemo) =>
  company.billings.filter((billing) =>
    withCreditMemo
      ? billing.AcctSeed__Type__c === 'Credit Memo'
      : billing.AcctSeed__Type__c !== 'Credit Memo',
  )

export const getBillingsSum = (company, withCreditMemo = false) =>
  getFilteredBillingsByType(company, withCreditMemo).reduce(
    (pre, billing) => pre + Number(billing.AcctSeed__Balance__c.toFixed(2)),
    0,
  )

export const categorizeDate = (dateStr) => {
  const today = new Date()
  const date = new Date(dateStr)
  const diffTime = Math.abs(today - date)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (date.getFullYear() === today.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  } else {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  }
}

// A processing fee from `/building/:id/processing-fee` carries both `percentage` and `flatAmount`
// (cents); `mode` decides which one applies. Fees saved before flat fees existed have no `mode`
// and are percentage-based.
export const getProcessingFeeCents = (billingAmountCents, processingFee) => {
  if (!processingFee || billingAmountCents <= 0) return 0

  if (processingFee.mode === 'flat') return Math.round(processingFee.flatAmount || 0)

  return Math.round((billingAmountCents * (processingFee.percentage || 0)) / 100)
}

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

const getRecordCharges = (record) => {
  const chargeCodes = record.chargeCodes || []
  if (chargeCodes.length === 0) {
    return record.description
      ? [{ id: 'description', amountCents: null, label: record.description }]
      : []
  }
  return chargeCodes.map((charge, index) => {
    const code = charge.chargeCode || charge.ChargeCode
    return {
      id: `${code || 'charge'}-${index}`,
      amountCents: typeof charge.amount === 'number' ? charge.amount : null,
      label: code ? capitalize(code) : record.description || '',
    }
  })
}

const buildHistoryItem = (record) => {
  const charges = getRecordCharges(record)

  const billingAmountCents =
    record.billingAmount ?? charges.reduce((sum, charge) => sum + (charge.amountCents || 0), 0)

  return {
    id: record.id,
    charges,
    isAutoPay: record.isAutoPay,
    paymentType: record.paymentType,
    // `totalAmount` is in cents and includes the processing fee.
    processingFee: billingAmountCents ? formatPrice(record.totalAmount - billingAmountCents) : null,
    status: record.status,
    totalAmount: formatPrice(record.totalAmount),
  }
}

export const groupPaymentHistory = (records) => {
  const sorted = [...(records || [])].sort((a, b) => b.createdAt - a.createdAt)

  const groups = new Map()
  sorted.forEach((record) => {
    const date = new Date(record.createdAt)
    const label = isToday(date) ? 'Today' : formatDateLong(date)

    if (!groups.has(label))
      groups.set(label, { dateGroup: label, items: [], sortKey: record.createdAt })
    groups.get(label).items.push(buildHistoryItem(record))
  })

  return [...groups.values()]
    .sort((a, b) => b.sortKey - a.sortKey)
    .map(({ dateGroup, items }) => ({ dateGroup, items }))
}

export const normalizeYardiCharges = (charges) =>
  (charges || []).map((charge) => ({
    ...charge,
    processingAmount: (charge.processingAmount ?? 0) / 100,
    transactions: (charge.transactions || []).map((transaction) => ({
      ...transaction,
      amount: transaction.amount / 100,
      amountPaid: transaction.amountPaid / 100,
      balanceDue: transaction.balanceDue / 100,
    })),
  }))

export const getChargeBalance = (charge) =>
  (charge.transactions || []).reduce((acc, transaction) => acc + transaction.balanceDue, 0)

export const getChargeProcessing = (charge) => charge.processingAmount ?? 0

// A roommate's pending ACH payment locks that part of the balance until it settles
// Rounded to cents — amounts are already converted to dollars, so float subtraction can drift
export const getChargePayable = (charge) =>
  Math.max(0, Math.round((getChargeBalance(charge) - getChargeProcessing(charge)) * 100) / 100)

export const isChargeLocked = (charge) =>
  getChargeProcessing(charge) > 0 && getChargePayable(charge) === 0

export const getChargesPayable = (charges) =>
  (charges || []).reduce((acc, charge) => acc + getChargePayable(charge), 0)

export const getChargesProcessing = (charges) =>
  (charges || []).reduce((acc, charge) => acc + getChargeProcessing(charge), 0)

// Spreads a custom "edit total" amount (cents) across charges in their listed order, capping
// each at its own amount — so what's charged matches the total the resident entered.
export const allocateAmount = (charges, totalCents) => {
  let remaining = Math.max(0, totalCents)
  return charges.map((charge) => {
    const amount = Math.min(charge.amount, remaining)
    remaining -= amount
    return { ...charge, amount }
  })
}

export const methodLabel = (t, paymentMethod) => {
  if (!paymentMethod) return null
  const { brand, last4, methodType } = paymentMethod

  if (methodType === 'ACH') {
    return brand
      ? t('organisation:payments.methodLabel.bankAccount', { brand, last4 })
      : t('organisation:payments.methodLabel.bankAccountWithoutBrand', { last4 })
  }

  return brand
    ? t('organisation:payments.methodLabel.card', { brand, last4 })
    : t('organisation:payments.methodLabel.cardWithoutBrand', { last4 })
}

// Stripe PaymentMethod.type values (e.g. "us_bank_account") all map to ACH.
export const isAchPaymentType = (paymentType) => {
  if (!paymentType) return false
  const normalized = paymentType.toLowerCase()
  return normalized === 'ach' || normalized.includes('bank_account')
}

export const paymentTypeLabel = (t, paymentType) => {
  if (!paymentType) return t('organisation:payments.history.paymentType.options.unknown')
  if (isAchPaymentType(paymentType)) {
    return t('organisation:payments.history.paymentType.options.ach')
  }
  const normalized = paymentType.toLowerCase()
  if (normalized === 'card' || normalized === 'creditcard') {
    return t('organisation:payments.history.paymentType.options.creditCard')
  }
  return paymentType
}

// Autopay charge day options — backend only accepts days 1-5 (StripeAutopayService.saveAutopay)
export const getDaysOfTheMonth = (t) => [
  { label: t('organisation:payments.payRentYardi.dayOfMonthOptions.first'), value: 1 },
  { label: t('organisation:payments.payRentYardi.dayOfMonthOptions.second'), value: 2 },
  { label: t('organisation:payments.payRentYardi.dayOfMonthOptions.third'), value: 3 },
  { label: t('organisation:payments.payRentYardi.dayOfMonthOptions.fourth'), value: 4 },
  { label: t('organisation:payments.payRentYardi.dayOfMonthOptions.fifth'), value: 5 },
]
